import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { fleetAPI, imageAPI } from '../../utils/api';
import { useToast } from '../../hooks/use-toast';
import { Pencil, Trash2, Plus, Upload } from 'lucide-react';

const EMPTY = { name: '', category: '', seating_capacity: 4, price_per_km: 0, image_url: '', features: '' };

export const FleetManager = () => {
  const { toast } = useToast();
  const [fleet, setFleet] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const res = await fleetAPI.getAll();
      setFleet(res.data);
    } catch (e) {
      console.error('Failed to load fleet', e);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const resetForm = () => { setForm(EMPTY); setEditingId(null); };

  const startEdit = (v) => {
    setEditingId(v.id);
    setForm({
      name: v.name, category: v.category, seating_capacity: v.seating_capacity,
      price_per_km: v.price_per_km, image_url: v.image_url, features: (v.features || []).join(', '),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      name: form.name,
      category: form.category,
      seating_capacity: parseInt(form.seating_capacity) || 0,
      price_per_km: parseFloat(form.price_per_km) || 0,
      image_url: form.image_url,
      features: form.features.split(',').map((f) => f.trim()).filter(Boolean),
    };
    try {
      if (editingId) {
        await fleetAPI.update(editingId, payload);
        toast({ title: 'Success', description: 'Vehicle updated' });
      } else {
        await fleetAPI.create(payload);
        toast({ title: 'Success', description: 'Vehicle added' });
      }
      resetForm();
      load();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to save vehicle', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this vehicle?')) return;
    try {
      await fleetAPI.remove(id);
      toast({ title: 'Deleted', description: 'Vehicle removed' });
      if (editingId === id) resetForm();
      load();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await imageAPI.upload(file);
      setForm((f) => ({ ...f, image_url: res.data.url }));
      toast({ title: 'Success', description: 'Image uploaded' });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to upload image', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-4">{editingId ? 'Edit Vehicle' : 'Add New Vehicle'}</h3>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="fleet-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="fleet-name-input" />
              </div>
              <div>
                <Label>Category</Label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} required data-testid="fleet-category-input" />
              </div>
              <div>
                <Label>Seating Capacity</Label>
                <Input type="number" value={form.seating_capacity} onChange={(e) => setForm({ ...form, seating_capacity: e.target.value })} required data-testid="fleet-seating-input" />
              </div>
              <div>
                <Label>Price per km (₹)</Label>
                <Input type="number" step="0.1" value={form.price_per_km} onChange={(e) => setForm({ ...form, price_per_km: e.target.value })} required data-testid="fleet-price-input" />
              </div>
            </div>
            <div>
              <Label>Vehicle Image</Label>
              <div className="flex items-center gap-3 mb-2">
                {form.image_url && <img src={form.image_url} alt="preview" className="h-14 w-14 object-cover rounded border" data-testid="fleet-image-preview" />}
                <label className="cursor-pointer">
                  <span className="inline-flex items-center px-3 py-2 bg-gray-900 text-white rounded-md text-sm hover:bg-gray-800">
                    <Upload className="h-4 w-4 mr-2" />{uploading ? 'Uploading...' : 'Upload Image'}
                  </span>
                  <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} disabled={uploading} data-testid="fleet-image-upload-input" />
                </label>
              </div>
              <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://... or upload above" required data-testid="fleet-image-input" />
            </div>
            <div>
              <Label>Features (comma separated)</Label>
              <Input value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} placeholder="AC, GPS, Music System" data-testid="fleet-features-input" />
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={saving} data-testid="fleet-save-button">
                {editingId ? 'Update Vehicle' : <><Plus className="h-4 w-4 mr-1" />Add Vehicle</>}
              </Button>
              {editingId && <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>}
            </div>
          </form>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {fleet.map((v) => (
          <div key={v.id} className="border rounded-lg p-4 flex items-start justify-between bg-white" data-testid={`fleet-row-${v.id}`}>
            <div className="flex gap-3">
              {v.image_url && <img src={v.image_url} alt={v.name} className="h-16 w-16 object-cover rounded" />}
              <div>
                <h4 className="font-semibold">{v.name}</h4>
                <p className="text-sm text-gray-600">{v.category} • {v.seating_capacity} seats • ₹{v.price_per_km}/km</p>
                <p className="text-xs text-gray-500 mt-1">{(v.features || []).join(' • ')}</p>
              </div>
            </div>
            <div className="flex gap-2 flex-shrink-0 ml-2">
              <Button size="sm" variant="outline" onClick={() => startEdit(v)} data-testid={`fleet-edit-${v.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button size="sm" variant="destructive" onClick={() => handleDelete(v.id)} data-testid={`fleet-delete-${v.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FleetManager;
